import {reportError} from '@/Services/Logger';

export interface IpData {
    country_code: string;
    city: string;
    country_name: string;
}

const FALLBACK: IpData = {country_code: 'in', city: '', country_name: ''};
const CACHE_KEY = 'ipgeo_cache';
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CacheEntry {
    data: IpData;
    expiresAt: number;
}

function readLocalCache(): IpData | null {
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return null;
        const entry: CacheEntry = JSON.parse(raw);
        if (Date.now() > entry.expiresAt) {
            localStorage.removeItem(CACHE_KEY);
            return null;
        }
        return entry.data;
    } catch {
        return null;
    }
}

function writeLocalCache(data: IpData): void {
    try {
        const entry: CacheEntry = {data, expiresAt: Date.now() + CACHE_TTL_MS};
        localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
        // localStorage may be unavailable (private browsing, storage quota exceeded)
    }
}

// In-memory cache — shared across all form mounts within the same page session.
// localStorage cache — survives page refreshes for up to 24 hours.
let cachedPromise: Promise<IpData> | null = null;

export function fetchIpData(): Promise<IpData> {
    if (cachedPromise) return cachedPromise;

    // Check localStorage before hitting the network
    if (globalThis.window !== undefined) {
        const local = readLocalCache();
        if (local) {
            cachedPromise = Promise.resolve(local);
            return cachedPromise;
        }
    }

    cachedPromise = fetch('https://ipapi.co/json/')
        .then((r) => r.json())
        .then((data) => {
            if (data.error) throw data;
            writeLocalCache(data as IpData);
            return data as IpData;
        })
        .catch((err) => {
            reportError(err);
            cachedPromise = null; // allow retry on next mount
            return FALLBACK;
        });

    return cachedPromise;
}
